import React, { createContext, useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, doc, addDoc, updateDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Role, SystemModule, ModulePermissions } from '@/types';

// Helper to create empty permissions object
// Copied from original useRoles.ts to maintain logic
export const createEmptyPermissions = (): Record<SystemModule, ModulePermissions> => ({
    dashboard_view: { create: false, read: false, update: false, delete: false },
    analytics_view: { create: false, read: false, update: false, delete: false },
    analytics_export: { create: false, read: false, update: false, delete: false },
    users_view: { create: false, read: false, update: false, delete: false },
    users_manage_students: { create: false, read: false, update: false, delete: false },
    users_manage_coaches: { create: false, read: false, update: false, delete: false },
    users_manage_staff: { create: false, read: false, update: false, delete: false },
    users_manage_parents: { create: false, read: false, update: false, delete: false },
    classes_view: { create: false, read: false, update: false, delete: false },
    classes_manage: { create: false, read: false, update: false, delete: false },
    schedule_view: { create: false, read: false, update: false, delete: false },
    schedule_manage: { create: false, read: false, update: false, delete: false },
    attendance_view: { create: false, read: false, update: false, delete: false },
    attendance_checkin: { create: false, read: false, update: false, delete: false },
    attendance_manage: { create: false, read: false, update: false, delete: false },
    finance_view: { create: false, read: false, update: false, delete: false },
    finance_income_view: { create: false, read: false, update: false, delete: false },
    finance_income_manage: { create: false, read: false, update: false, delete: false },
    finance_expenses_view: { create: false, read: false, update: false, delete: false },
    finance_expenses_manage: { create: false, read: false, update: false, delete: false },
    finance_payouts_view: { create: false, read: false, update: false, delete: false },
    finance_payouts_manage: { create: false, read: false, update: false, delete: false },
    finance_settings: { create: false, read: false, update: false, delete: false },
    wallet_view: { create: false, read: false, update: false, delete: false },
    wallet_manage: { create: false, read: false, update: false, delete: false },
    subscriptions_view: { create: false, read: false, update: false, delete: false },
    subscriptions_manage: { create: false, read: false, update: false, delete: false },
    subscriptions_plans: { create: false, read: false, update: false, delete: false },
    gamification_view: { create: false, read: false, update: false, delete: false },
    gamification_manage: { create: false, read: false, update: false, delete: false },
    gamification_belts: { create: false, read: false, update: false, delete: false },
    leaderboard_view: { create: false, read: false, update: false, delete: false },
    challenges_view: { create: false, read: false, update: false, delete: false },
    challenges_manage: { create: false, read: false, update: false, delete: false },
    challenges_participate: { create: false, read: false, update: false, delete: false },
    lms_view: { create: false, read: false, update: false, delete: false },
    lms_manage: { create: false, read: false, update: false, delete: false },
    kb_view: { create: false, read: false, update: false, delete: false },
    kb_manage: { create: false, read: false, update: false, delete: false },
    branches_view: { create: false, read: false, update: false, delete: false },
    branches_manage: { create: false, read: false, update: false, delete: false },
    roles_view: { create: false, read: false, update: false, delete: false },
    roles_manage: { create: false, read: false, update: false, delete: false },
    audit_view: { create: false, read: false, update: false, delete: false },
    audit_manage: { create: false, read: false, update: false, delete: false },
    settings_general: { create: false, read: false, update: false, delete: false },
    settings_profile: { create: false, read: true, update: true, delete: false },
    feed_view: { create: false, read: false, update: false, delete: false },
    feed_post: { create: false, read: false, update: false, delete: false },
    feed_moderate: { create: false, read: false, update: false, delete: false },
    messages_view: { create: false, read: false, update: false, delete: false },
    messages_send: { create: false, read: false, update: false, delete: false },
    notifications_view: { create: false, read: false, update: false, delete: false },
    notifications_manage: { create: false, read: false, update: false, delete: false },
    support_view: { create: false, read: false, update: false, delete: false },
    support_inbox: { create: false, read: false, update: false, delete: false },
    support_reply: { create: false, read: false, update: false, delete: false },
    files_view: { create: false, read: false, update: false, delete: false },
    files_manage: { create: false, read: false, update: false, delete: false },
    whatsapp_send: { create: false, read: false, update: false, delete: false },
    whatsapp_templates_manage: { create: false, read: false, update: false, delete: false },
});

// Define permission groups based on SystemModule for easier management
export const PERMISSION_GROUPS = {
    dashboard: { name: 'Dashboard', modules: ['dashboard_view'] as SystemModule[] },
    analytics: { name: 'Analytics', modules: ['analytics_view', 'analytics_export'] as SystemModule[] },
    users: { name: 'Users', modules: ['users_view', 'users_manage_students', 'users_manage_coaches', 'users_manage_staff', 'users_manage_parents'] as SystemModule[] },
    classes: { name: 'Classes', modules: ['classes_view', 'classes_manage'] as SystemModule[] },
    schedule: { name: 'Schedule', modules: ['schedule_view', 'schedule_manage'] as SystemModule[] },
    attendance: { name: 'Attendance', modules: ['attendance_view', 'attendance_checkin', 'attendance_manage'] as SystemModule[] },
    finance: { name: 'Finance', modules: ['finance_view', 'finance_income_view', 'finance_income_manage', 'finance_expenses_view', 'finance_expenses_manage', 'finance_payouts_view', 'finance_payouts_manage', 'finance_settings'] as SystemModule[] },
    wallet: { name: 'Wallet', modules: ['wallet_view', 'wallet_manage'] as SystemModule[] },
    subscriptions: { name: 'Subscriptions', modules: ['subscriptions_view', 'subscriptions_manage', 'subscriptions_plans'] as SystemModule[] },
    gamification: { name: 'Gamification', modules: ['gamification_view', 'gamification_manage', 'gamification_belts', 'leaderboard_view', 'challenges_view', 'challenges_manage', 'challenges_participate'] as SystemModule[] },
    lms: { name: 'LMS', modules: ['lms_view', 'lms_manage'] as SystemModule[] },
    kb: { name: 'Knowledge Base', modules: ['kb_view', 'kb_manage'] as SystemModule[] },
    branches: { name: 'Branches', modules: ['branches_view', 'branches_manage'] as SystemModule[] },
    roles: { name: 'Roles', modules: ['roles_view', 'roles_manage'] as SystemModule[] },
    audit: { name: 'Audit Log', modules: ['audit_view', 'audit_manage'] as SystemModule[] },
    settings: { name: 'Settings', modules: ['settings_general', 'settings_profile'] as SystemModule[] },
    feed: { name: 'Feed', modules: ['feed_view', 'feed_post', 'feed_moderate'] as SystemModule[] },
    messages: { name: 'Messages', modules: ['messages_view', 'messages_send'] as SystemModule[] },
    notifications: { name: 'Notifications', modules: ['notifications_view', 'notifications_manage'] as SystemModule[] },
    support: { name: 'Support', modules: ['support_view', 'support_inbox', 'support_reply'] as SystemModule[] },
    files: { name: 'Files', modules: ['files_view', 'files_manage'] as SystemModule[] },
};

// Default system roles configuration
export const DEFAULT_SYSTEM_ROLES: Omit<Role, 'createdAt' | 'updatedAt'>[] = [
    {
        id: 'super-admin',
        name: 'Super Admin',
        description: 'Full access to all system features',
        permissions: Object.keys(PERMISSION_GROUPS).reduce((acc, group) => {
            PERMISSION_GROUPS[group as keyof typeof PERMISSION_GROUPS].modules.forEach(module => {
                acc[module] = {
                    create: true, read: true, update: true, delete: true,
                    approve: true, reject: true, assign: true, export: true,
                    import: true, impersonate: true, configure: true, moderate: true
                };
            });
            return acc;
        }, {} as Record<SystemModule, ModulePermissions>),
        isSystem: true,
        isGamified: true,
        icon: 'Crown',
        color: '#ef4444',
    },
    {
        id: 'coach',
        name: 'Coach',
        description: 'Manage classes, attendance, and student progress',
        permissions: {
            ...createEmptyPermissions(),
            dashboard_view: { create: false, read: true, update: false, delete: false },
            users_view: { create: false, read: true, update: false, delete: false },
            users_manage_students: { create: false, read: true, update: true, delete: false },
            classes_view: { create: false, read: true, update: false, delete: false },
            schedule_view: { create: false, read: true, update: false, delete: false },
            attendance_view: { create: false, read: true, update: false, delete: false },
            attendance_checkin: { create: true, read: true, update: true, delete: false }, // Can check in
            attendance_manage: { create: true, read: true, update: true, delete: false },
            lms_view: { create: false, read: true, update: false, delete: false },
            lms_manage: { create: true, read: true, update: true, delete: false }, // Can manage content
            feed_view: { create: false, read: true, update: false, delete: false },
            feed_post: { create: true, read: true, update: true, delete: true },
            messages_view: { create: false, read: true, update: false, delete: false },
            messages_send: { create: true, read: true, update: false, delete: false },
            finance_payouts_view: { create: false, read: true, update: false, delete: false }, // Own payouts
            settings_profile: { create: false, read: true, update: true, delete: false },
        },
        isSystem: true,
        isGamified: true,
        icon: 'Briefcase',
        color: '#f97316',
    },
    {
        id: 'student',
        name: 'Student',
        description: 'Access to learning materials, schedule, and progress tracking',
        permissions: {
            ...createEmptyPermissions(),
            dashboard_view: { create: false, read: true, update: false, delete: false },
            classes_view: { create: false, read: true, update: false, delete: false },
            schedule_view: { create: false, read: true, update: false, delete: false },
            attendance_view: { create: false, read: true, update: false, delete: false }, // Own attendance
            lms_view: { create: false, read: true, update: false, delete: false }, // Consume content
            feed_view: { create: false, read: true, update: false, delete: false },
            feed_post: { create: true, read: true, update: true, delete: true },
            messages_view: { create: false, read: true, update: false, delete: false },
            messages_send: { create: true, read: true, update: false, delete: false },
            leaderboard_view: { create: false, read: true, update: false, delete: false },
            challenges_view: { create: false, read: true, update: false, delete: false },
            challenges_participate: { create: true, read: true, update: true, delete: false },
            settings_profile: { create: false, read: true, update: true, delete: false },
            gamification_view: { create: false, read: true, update: false, delete: false }, // View badges
            wallet_view: { create: false, read: true, update: false, delete: false }, // Own wallet
        },
        isSystem: true,
        isGamified: true,
        icon: 'GraduationCap',
        color: '#3b82f6',
    },
    {
        id: 'parent',
        name: 'Parent',
        description: 'Monitor child progress and manage subscriptions',
        permissions: {
            ...createEmptyPermissions(),
            files_manage: { create: false, read: false, update: false, delete: false }, // override default just in case
            dashboard_view: { create: false, read: true, update: false, delete: false },
            schedule_view: { create: false, read: true, update: false, delete: false },
            attendance_view: { create: false, read: true, update: false, delete: false }, // Child's attendance
            finance_view: { create: false, read: true, update: false, delete: false }, // Billing
            subscriptions_view: { create: false, read: true, update: false, delete: false },
            feed_view: { create: false, read: true, update: false, delete: false }, // Usually read-only or interaction
            settings_profile: { create: false, read: true, update: true, delete: false },
            wallet_view: { create: false, read: true, update: false, delete: false }, // Family wallet
        },
        isSystem: true,
        isGamified: false,
        icon: 'Heart',
        color: '#ec4899',
    },
    {
        id: 'receptionist',
        name: 'Receptionist',
        description: 'Front desk operations, attendance, and basic user management',
        permissions: {
            ...createEmptyPermissions(),
            dashboard_view: { create: false, read: true, update: false, delete: false },
            users_view: { create: false, read: true, update: false, delete: false },
            users_manage_students: { create: true, read: true, update: true, delete: false },
            users_manage_parents: { create: true, read: true, update: true, delete: false },
            classes_view: { create: false, read: true, update: false, delete: false },
            schedule_view: { create: false, read: true, update: false, delete: false },
            attendance_view: { create: false, read: true, update: false, delete: false },
            attendance_checkin: { create: true, read: true, update: true, delete: false },
            attendance_manage: { create: true, read: true, update: true, delete: false },
            finance_income_manage: { create: true, read: true, update: true, delete: false }, // Collect payments
            settings_profile: { create: false, read: true, update: true, delete: false },
            support_view: { create: false, read: true, update: false, delete: false },
            support_reply: { create: true, read: true, update: true, delete: false },
        },
        isSystem: true,
        isGamified: true,
        icon: 'Star',
        color: '#eab308',
    },
    {
        id: 'coordinator',
        name: 'Coordinator',
        description: 'manage schedules, enrollments, and support',
        permissions: {
            ...createEmptyPermissions(),
            dashboard_view: { create: false, read: true, update: false, delete: false },
            users_view: { create: false, read: true, update: false, delete: false },
            users_manage_students: { create: true, read: true, update: true, delete: false },
            users_manage_parents: { create: true, read: true, update: true, delete: false },
            // Coordinator specific enhancements
            classes_view: { create: false, read: true, update: false, delete: false },
            classes_manage: { create: false, read: true, update: true, delete: false }, // Edit classes
            schedule_view: { create: false, read: true, update: false, delete: false },
            schedule_manage: { create: true, read: true, update: true, delete: true }, // Manage schedule
            subscriptions_view: { create: false, read: true, update: false, delete: false },
            subscriptions_manage: { create: true, read: true, update: true, delete: false },
            support_view: { create: false, read: true, update: false, delete: false },
            support_reply: { create: true, read: true, update: true, delete: false },
            settings_profile: { create: false, read: true, update: true, delete: false },
        },
        isSystem: true,
        isGamified: true,
        icon: 'Settings',
        color: '#a855f7',
    }
];

interface RolesContextType {
    roles: Role[];
    loading: boolean;
    error: string | null;
    fetchRoles: () => Promise<void>;
    createRole: (roleData: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
    updateRole: (roleId: string, updates: Partial<Omit<Role, 'id' | 'createdAt'>>) => Promise<void>;
    deleteRole: (roleId: string) => Promise<void>;
    getRoleById: (roleId: string) => Role | undefined;
    seedDefaultRoles: () => Promise<{ created: number; skipped: number }>;
}

export const RolesContext = createContext<RolesContextType | undefined>(undefined);

export const RolesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const toDate = (val: unknown): Date => {
        if (!val) return new Date();
        if (typeof val === 'object' && val !== null && 'toDate' in val && typeof (val as { toDate: () => Date }).toDate === 'function') {
            return (val as { toDate: () => Date }).toDate();
        }
        if (val instanceof Date) return val;
        return new Date(val as string | number);
    };

    const fetchRoles = useCallback(async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'roles'), orderBy('createdAt', 'asc'));
            const querySnapshot = await getDocs(q);
            const fetchedRoles: Role[] = [];

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                // Basic mapping without the heavy backfill logic for now (can restore if needed)
                fetchedRoles.push({
                    id: doc.id,
                    name: data.name,
                    description: data.description,
                    permissions: data.permissions || createEmptyPermissions(), // Ensure perms exist
                    isSystem: data.isSystem,
                    createdAt: toDate(data.createdAt),
                    updatedAt: toDate(data.updatedAt),
                    isGamified: data.isGamified ?? true,
                    icon: data.icon, // CRITICAL: Ensure icon is mapped
                    color: data.color, // CRITICAL: Ensure color is mapped
                });
            });

            setRoles(fetchedRoles);
            setError(null);
        } catch (err) {
            console.error('Error fetching roles:', err);
            setError('Failed to fetch roles');
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    const createRole = async (roleData: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>) => {
        try {
            const docRef = await addDoc(collection(db, 'roles'), {
                ...roleData,
                createdAt: new Date(),
                updatedAt: new Date(),
            });
            await fetchRoles(); // Refresh global state
            return docRef.id;
        } catch (err) {
            console.error('Error creating role:', err);
            throw err;
        }
    };

    const updateRole = async (roleId: string, updates: Partial<Omit<Role, 'id' | 'createdAt'>>) => {
        try {
            await updateDoc(doc(db, 'roles', roleId), {
                ...updates,
                updatedAt: new Date(),
            });
            await fetchRoles(); // Refresh global state
        } catch (err) {
            console.error('Error updating role:', err);
            throw err;
        }
    };

    const deleteRole = async (roleId: string) => {
        const role = roles.find(r => r.id === roleId);
        if (role?.isSystem) throw new Error('Cannot delete system roles');

        try {
            await deleteDoc(doc(db, 'roles', roleId));
            await fetchRoles(); // Refresh global state
        } catch (err) {
            console.error('Error deleting role:', err);
            throw err;
        }
    };

    const getRoleById = (roleId: string) => roles.find(r => r.id === roleId);

    // Seed default system roles if they don't exist
    const seedDefaultRoles = async (): Promise<{ created: number; skipped: number }> => {
        let created = 0;
        let skipped = 0;

        try {
            // Fetch current roles to check which exist
            const q = query(collection(db, 'roles'));
            const snapshot = await getDocs(q);
            const existingRoleNames = new Set<string>();
            snapshot.forEach(doc => {
                existingRoleNames.add(doc.data().name.toLowerCase());
            });

            // Create missing default roles
            for (const roleData of DEFAULT_SYSTEM_ROLES) {
                if (existingRoleNames.has(roleData.name.toLowerCase())) {
                    skipped++;
                    continue;
                }

                await addDoc(collection(db, 'roles'), {
                    ...roleData,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });
                created++;
            }

            // Refresh roles list
            await fetchRoles();
            return { created, skipped };
        } catch (err) {
            console.error('Error seeding default roles:', err);
            throw err;
        }
    };

    const value = {
        roles,
        loading,
        error,
        fetchRoles,
        createRole,
        updateRole,
        deleteRole,
        getRoleById,
        seedDefaultRoles
    };

    return <RolesContext.Provider value={value}>{children}</RolesContext.Provider>;
};
